import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import { SelectField } from 'components/form';
import { CustomerVisibility } from 'modules/dealSheet';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChargeBucketRef, ChargeParentKind } from 'modules/equipment/types';
import { blue, grey } from '@mui/material/colors';
import { ListingForm } from 'components/shared/TableListing/types';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IndicatorMenu, IndicatorMenuOption } from 'components/shared/IndicatorMenu';

import type { ChargeListingService } from '../../../../../lib';
import { QuoteChargeListingEntity } from '../../../../../types';

interface Props {
  rowIndex: number;
  scope: ChargeBucketRef;
  chargeService: ChargeListingService;
}

const VISIBILITY_OPTIONS: IndicatorMenuOption<CustomerVisibility>[] = [
  {
    color: grey[500],
    label: 'Visible to customer',
    value: CustomerVisibility.Visible,
    renderIcon: (color: string) => <Visibility sx={{ color, fontSize: '1rem' }} />,
  },
  {
    color: blue[500],
    label: 'Internal only',
    value: CustomerVisibility.InternalOnly,
    renderIcon: (color: string) => <VisibilityOff sx={{ color, fontSize: '1rem' }} />,
  },
];

export const LineItemVisibility = ({ rowIndex, scope, chargeService }: Props) => {
  const { control, setValue } = useFormContext<ListingForm<QuoteChargeListingEntity>>();

  const rows = useWatch({ control, name: 'bodyFields' });
  const customerVisibility = useWatch({
    control,
    name: `bodyFields.${rowIndex}.charge.customerVisibility`,
  });

  const currentCharge = rows?.[rowIndex]?.charge;

  const parentOptions = useMemo(
    () => (rows ? chargeService.buildParentOptions({ rows, currentRowIndex: rowIndex, scope }) : []),
    [rows, rowIndex, scope, chargeService]
  );

  /** Map parentId → kind so we can atomically persist both fields on select. */
  const kindByValue = useMemo(() => {
    const map = new Map<string, ChargeParentKind>();
    parentOptions.forEach(option => map.set(String(option.value), option.kind));
    return map;
  }, [parentOptions]);

  const handleVisibilityChange = (next: CustomerVisibility) => {
    setValue(`bodyFields.${rowIndex}.charge.customerVisibility`, next, { shouldValidate: true });

    if (next !== CustomerVisibility.InternalOnly) {
      setValue(`bodyFields.${rowIndex}.charge.parentId`, null, { shouldValidate: true });
      setValue(`bodyFields.${rowIndex}.charge.parentType`, undefined, { shouldValidate: true });
    }
  };

  const handleParentPicked = (nextValue: string | number | boolean) => {
    const kind = kindByValue.get(String(nextValue));
    setValue(`bodyFields.${rowIndex}.charge.parentType`, kind ?? 'charge', { shouldValidate: true });
  };

  if (!currentCharge) return null;

  const isInternalOnly = customerVisibility === CustomerVisibility.InternalOnly;

  return (
    <Stack gap="0.5rem">
      <IndicatorMenu
        options={VISIBILITY_OPTIONS}
        onChange={handleVisibilityChange}
        value={customerVisibility ?? CustomerVisibility.Visible}
      />

      {isInternalOnly && (
        <SelectField
          control={control}
          variant="outlined"
          label="Roll up into"
          options={parentOptions}
          extraAction={handleParentPicked}
          name={`bodyFields.${rowIndex}.charge.parentId`}
        />
      )}
    </Stack>
  );
};
